import type { MaybeArray, ObjectType } from "models-webgl2";
import { toArrayOptions } from "utils/helpers/to-array-options";
import { makeAutoBindFunctions } from "utils/helpers/make-auto-bind-functions";
import type { Shader, ShaderUniformKeys, MapShaderUniforms } from "../shader";
import { Texture } from "../texture";
import {
  MaterialAllowedUniforms,
  MaterialAllowedUniformsKeys,
  MaterialTexturesUniforms,
} from "./material.types";

export class Material<S extends Shader = Shader<any, any>> {
  public readonly shader: S;

  public uniforms: MapShaderUniforms<S> = {
    u_opacity: 1,
  } as any;

  public textures: Partial<ObjectType<Texture, keyof MaterialTexturesUniforms<S>>> = {};

  constructor(shader: S, uniforms?: Partial<MaterialAllowedUniforms<S>>) {
    this.shader = shader;
    if (uniforms) this.setUniforms(uniforms);
    makeAutoBindFunctions(this);
  }

  public clone(uniforms?: Partial<MaterialAllowedUniforms<S>>) {
    return new Material(this.shader, uniforms);
  }

  public setUniform<K extends MaterialAllowedUniformsKeys<S>>(
    key: K,
    value: MaterialAllowedUniforms<S>[K],
  ) {
    this.uniforms[key as keyof MapShaderUniforms<S>] = value as any;
  }

  public setUniforms(uniforms: Partial<MaterialAllowedUniforms<S>>) {
    for (const key in uniforms) {
      this.uniforms[key] = uniforms[key] as any;
    }
  }

  public removeUniform(key: ShaderUniformKeys<S>) {
    delete this.uniforms[key];
  }

  public removeUniforms(uniforms: MaybeArray<ShaderUniformKeys<S>>) {
    for (const key of toArrayOptions(uniforms)) {
      delete this.uniforms[key];
    }
  }

  public setTexture(name: keyof MaterialTexturesUniforms<S>, texture: Texture) {
    const prevTexture = this.textures[name];
    if (!texture || prevTexture === texture) return;

    // name is u_texture0, u_texture1 ... u_texture(N)
    const textureNumber = String(name).slice(9);
    const existName = "u_textureExist" + textureNumber;

    this.textures[name] = texture;
    this.uniforms[name as keyof MapShaderUniforms<S>] = texture.id as any;
    this.uniforms[existName as keyof MapShaderUniforms<S>] = true as any;
  }

  public setTextures(textures: Partial<ObjectType<Texture, keyof MaterialTexturesUniforms<S>>>) {
    for (const key in textures) {
      const texture = textures[key];
      if (texture) this.setTexture(key, texture);
    }
  }

  public removeTexture(name: keyof MaterialTexturesUniforms<S>) {
    const texture = this.textures[name];
    if (!texture) return;

    // name is u_texture0, u_texture1 ... u_texture(N)
    const textureNumber = String(name).slice(9);
    const existName = "u_textureExist" + textureNumber;

    delete this.textures[name];
    delete this.uniforms[name as keyof MapShaderUniforms<S>];
    delete this.uniforms[existName as keyof MapShaderUniforms<S>];
  }

  public removeTextures(names: MaybeArray<keyof MaterialTexturesUniforms<S>>) {
    for (const name of toArrayOptions(names)) {
      this.removeTexture(name);
    }
  }

  public use(uniforms: MaterialAllowedUniforms<S>) {
    this.setUniforms(uniforms as any);
    this.bind();
  }

  public clearMaterial() {
    this.uniforms = { u_opacity: 1 } as any;
    this.textures = {} as any;
  }

  public bind() {
    this.shader.bind();
    this.shader.setUniforms(this.uniforms);
  }

  public unbind() {
    this.shader.unbind();
  }
}

export default Material;
